/**
 * The browser half of `/ws`. Task 33.
 *
 * Framework-free on purpose — no React, no TanStack — so the reconnect arithmetic and the
 * re-subscription bookkeeping are unit-testable without a DOM. `LiveProvider` is the thin
 * React layer over it.
 *
 * ## What `API-Spec.md#websocket` requires of a client, and where each part is
 *
 * > automatic reconnect with backoff and re-subscription; **on reconnect the client refetches
 * > state over REST rather than assuming continuity**, because a gap in a push stream is
 * > invisible unless you check.
 *
 * The reconnect and the re-subscription are here. **The refetch is not**, and that separation
 * is deliberate: this module does not know what a query cache is, so it reports the reconnect
 * through `onReconnect` and `LiveProvider` invalidates. A socket that reached into the cache
 * would be a socket that has to be mocked to test either half.
 *
 * The rule itself is the important part. The transport is Redis pub/sub end to end (see
 * `jmt_api/hub.py` for why a durable consumer group would be *wrong* here), so a gap is
 * genuinely unrecoverable from the stream. Anything on screen after a reconnect that was not
 * refetched is a number of unknown age presented as current — the one defect the design
 * register calls the worst this product can have.
 *
 * ## A session that ended is not a connection that dropped
 *
 * The server closes with `4401` when the session is revoked or expired. Retrying that is a
 * request storm against a door that will not open, and worse, it hides the real state: the
 * operator sees "reconnecting…" where the truth is "you are signed out". So `4401` stops the
 * loop and reports it, and `AuthProvider`'s existing `sessionLost` path takes over — the same
 * single handler every 401 already routes through, rather than a second opinion beside it.
 */

/** Frames the server sends. A push is the one shape with no `type` — see `hub._push_frame`. */
export interface PushFrame {
  topic: string;
  data: Record<string, unknown>;
}

export interface ControlFrame {
  type: 'subscribed' | 'unsubscribed' | 'ping' | 'error' | 'logs.dropped';
  topics?: string[];
  unavailable?: string[];
  code?: string;
  detail?: string;
  /** `logs.dropped` only: how many event-shaped frames the server's outbox discarded. */
  count?: number;
}

export type ConnectionState = 'connecting' | 'open' | 'closed' | 'signed-out';

/** Close code the server uses when the session behind the socket has died. */
export const CLOSE_SESSION_ENDED = 4401;

/**
 * Reconnect delays, in order, with the last repeating. **No jitter, deliberately.**
 *
 * Jitter exists to stop a thundering herd, and this is a single-operator system behind
 * Tailscale — there is no herd. What the absence buys is a deterministic test: `socket.test.ts`
 * asserts the schedule, which it could not do against a randomised one.
 */
export const BACKOFF_MS = [500, 1_000, 2_000, 5_000, 10_000, 15_000] as const;

export type PushHandler = (data: Record<string, unknown>) => void;

export interface LiveSocketOptions {
  /** Injected so a test can drive a fake. Defaults to the global. */
  readonly socketFactory?: (url: string) => WebSocket;
  /** Injected for the same reason; must return a handle `clearTimeout` accepts. */
  readonly setTimeoutFn?: typeof setTimeout;
  readonly clearTimeoutFn?: typeof clearTimeout;
  /** Called after a reconnect, so the app can refetch over REST. See the module docstring. */
  readonly onReconnect?: () => void;
  readonly onState?: (state: ConnectionState) => void;
  /** Topics the server accepted but cannot deliver yet — `topics.SERVED_PREFIXES`. */
  readonly onUnavailable?: (topics: string[]) => void;
  /** The server's log outbox overflowed and discarded this many frames. */
  readonly onLogsDropped?: (count: number) => void;
  readonly onSessionEnded?: () => void;
}

/**
 * The socket URL for this page.
 *
 * `wss` when the page is `https`, which is not a nicety: a browser refuses a plain `ws://`
 * from a secure page as mixed content, and the failure is a console line nobody reads rather
 * than an error the app can show. Same-origin, matching `api/client.ts` — the session cookie
 * is `SameSite=Strict` and a cross-origin handshake would not carry it.
 */
export function socketUrl(location: { protocol: string; host: string } = window.location): string {
  const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${location.host}/api/v1/ws`;
}

export class LiveSocket {
  private socket: WebSocket | null = null;
  private readonly handlers = new Map<string, Set<PushHandler>>();
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private everConnected = false;
  private state: ConnectionState = 'closed';

  private readonly options: LiveSocketOptions;
  private readonly url: string;

  constructor(url: string = socketUrl(), options: LiveSocketOptions = {}) {
    this.url = url;
    this.options = options;
  }

  get connectionState(): ConnectionState {
    return this.state;
  }

  /** Topics with at least one live handler. The set replayed on every reconnect. */
  get topics(): string[] {
    return [...this.handlers.keys()];
  }

  start(): void {
    this.stopped = false;
    this.open();
  }

  /** Close and stop reconnecting. Idempotent. */
  stop(): void {
    this.stopped = true;
    this.clearTimer();
    const socket = this.socket;
    this.socket = null;
    // Handlers removed before closing, so the `onclose` this triggers cannot schedule a
    // reconnect for a socket the caller has just asked to stop.
    if (socket) {
      socket.onopen = null;
      socket.onclose = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.close();
    }
    this.setState('closed');
  }

  /**
   * Listen to one topic. Returns the unsubscribe.
   *
   * Reference-counted: `subscribe` goes to the server when a topic gains its first handler and
   * `unsubscribe` when it loses its last. Two components watching the same symbol is ordinary
   * — the chart and a price header would — and sending a second `subscribe` for a topic already
   * held is harmless, while sending `unsubscribe` when only one of the two unmounted would
   * silently stop the other.
   */
  on(topic: string, handler: PushHandler): () => void {
    const existing = this.handlers.get(topic);
    if (existing) {
      existing.add(handler);
    } else {
      this.handlers.set(topic, new Set([handler]));
      this.send({ action: 'subscribe', topics: [topic] });
    }
    return () => {
      const handlers = this.handlers.get(topic);
      if (!handlers) return;
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.handlers.delete(topic);
        this.send({ action: 'unsubscribe', topics: [topic] });
      }
    };
  }

  // --- internals ---------------------------------------------------------------

  private open(): void {
    if (this.stopped) return;
    this.clearTimer();
    this.setState('connecting');

    const factory = this.options.socketFactory ?? ((url: string) => new WebSocket(url));
    const socket = factory(this.url);
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.setState('open');
      // Every topic, in one frame. The server's subscription set starts empty on a new socket,
      // so a client that only replayed "topics added since the drop" would come back listening
      // to nothing while looking connected.
      if (this.handlers.size > 0) {
        this.send({ action: 'subscribe', topics: [...this.handlers.keys()] });
      }
      // Only after a *re*connect. On the first connection there is nothing to have missed, and
      // firing it would refetch every query immediately after the page already loaded them.
      if (this.everConnected) this.options.onReconnect?.();
      this.everConnected = true;
    };

    socket.onmessage = (event: MessageEvent) => {
      this.receive(typeof event.data === 'string' ? event.data : '');
    };

    socket.onclose = (event: CloseEvent) => {
      this.socket = null;
      if (event.code === CLOSE_SESSION_ENDED) {
        // Not a dropped connection. Retrying would be a request storm against a door that will
        // not open, and "reconnecting…" is the wrong thing to show someone who is signed out.
        this.stopped = true;
        this.setState('signed-out');
        this.options.onSessionEnded?.();
        return;
      }
      this.setState('closed');
      this.scheduleReconnect();
    };

    // `onerror` is followed by `onclose` in every browser, so the reconnect is scheduled there
    // and only there. Handling both would double every backoff step.
    socket.onerror = () => {};
  }

  private receive(raw: string): void {
    let frame: PushFrame | ControlFrame;
    try {
      frame = JSON.parse(raw) as PushFrame | ControlFrame;
    } catch {
      return;
    }
    if ('topic' in frame && typeof frame.topic === 'string') {
      const handlers = this.handlers.get(frame.topic);
      if (!handlers) return;
      // Copied before iterating: a handler that unsubscribes itself while being called would
      // otherwise mutate the set mid-iteration.
      for (const handler of [...handlers]) handler(frame.data);
      return;
    }
    const control = frame as ControlFrame;
    if (control.type === 'ping') {
      this.send({ action: 'pong' });
      return;
    }
    if (control.type === 'subscribed' && control.unavailable?.length) {
      // Reported rather than swallowed: a real topic with no producer is a thing the UI can
      // say, where a silent acceptance makes "quiet" and "not built" the same observation.
      this.options.onUnavailable?.(control.unavailable);
      return;
    }
    if (control.type === 'logs.dropped' && typeof control.count === 'number') {
      // A control frame rather than a push because it is not something the system logged — it
      // is the server saying it could not keep up. It still belongs *in* the stream, since it
      // marks a real gap, so it is routed to the terminal rather than counted somewhere quiet.
      this.options.onLogsDropped?.(control.count);
    }
  }

  private send(message: Record<string, unknown>): void {
    // Dropped when the socket is not open, and that is correct rather than lossy: the whole
    // subscription set is replayed on `onopen`, so a `subscribe` sent while reconnecting would
    // be sent again a moment later anyway.
    if (this.socket?.readyState !== 1) return;
    this.socket.send(JSON.stringify(message));
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    const delay = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)];
    this.attempt += 1;
    const schedule = this.options.setTimeoutFn ?? setTimeout;
    this.timer = schedule(() => {
      this.timer = null;
      this.open();
    }, delay);
  }

  private clearTimer(): void {
    if (this.timer === null) return;
    const cancel = this.options.clearTimeoutFn ?? clearTimeout;
    cancel(this.timer);
    this.timer = null;
  }

  private setState(state: ConnectionState): void {
    if (this.state === state) return;
    this.state = state;
    this.options.onState?.(state);
  }
}
